import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import classnames from 'classnames/bind'
import { Button, Icon, ListingCarousel, ScrollSpy, Text } from '@adc/ui-adc'
import { useListingContext } from 'common/context/listing'
import { usePropertyContext } from 'common/context/property'
import analytics from '../../services/analytics'
import SectionPanel from '../../components/sectionPanel'
import { VIEW_SIMILAR_PROPERTIES, SIMILAR_PROPERTY_SOURCE } from '../../statics'
import hasSimilarProperties from '../../utils/hasSimilarProperties'
import { useModalFeatures } from '../../hooks/useModalFeatures'
import styles from './index.scss'

const cx = classnames.bind(styles)

const SIMILAR_PROPERTIES_ID = 'similar_properties'

const SimilarPropertiesCarousel = ({
  similarProperties = [],
  featureFlags,
}) => {
  const { pushAuthQueueItem, toggleAuthModal } = useModalFeatures()
  const { listing } = useListingContext()
  const { propertyActions, savedPropertiesData } = usePropertyContext()

  const similarPropertiesWithSaveState = useMemo(() => {
    if (!savedPropertiesData) return similarProperties

    return similarProperties.map(listing => {
      const primaryPropertySaved =
        savedPropertiesData.savedProperties.mapped.get(
          listing.primary_property.property_id
        )

      return {
        ...listing,
        primary_property: {
          ...listing.primary_property,
          ...primaryPropertySaved,
        },
      }
    })
  }, [similarProperties, savedPropertiesData])

  const onViewNowClick = useCallback(
    carouselIndex => {
      const targetListing = similarPropertiesWithSaveState[carouselIndex]
      return analytics.property.trackSimilarPropertyClick(listing, {
        carouselIndex,
        destinationPropertyId: targetListing.listing_id,
        destinationProductType:
          targetListing.listing_configuration.product_type,
      })
    },
    [listing, similarPropertiesWithSaveState]
  )

  const onSaveToggle = propertyBody => {
    const similarListing = similarPropertiesWithSaveState.find(
      l => l.primary_property.property_id === propertyBody.property_id
    )

    propertyActions.toggleSaveProperty({
      property: propertyBody,
      listing: similarListing || listing,
      source: SIMILAR_PROPERTY_SOURCE,
      pushAuthQueueItem,
      toggleAuthModal,
    })
  }

  return (
    hasSimilarProperties(similarPropertiesWithSaveState) && (
      <SectionPanel elmId={SIMILAR_PROPERTIES_ID} title='Similar Properties'>
        <div className={cx('similar-properties-container')}>
          <Text color='gray' size='large' className='u-mb-2'>
            Based on what you and others are looking at on Auction.com, here's a
            few recommendations.
          </Text>

          <ListingCarousel
            listings={similarPropertiesWithSaveState}
            showSave
            showV2Card
            elmId={SIMILAR_PROPERTIES_ID}
            onSaveToggle={onSaveToggle}
            analytics={{
              click: onViewNowClick,
            }}
            arrowIconPropsSettings={{
              elmId: SIMILAR_PROPERTIES_ID,
            }}
            featureFlags={featureFlags}
          />
        </div>
      </SectionPanel>
    )
  )
}

SimilarPropertiesCarousel.propTypes = {
  similarProperties: PropTypes.arrayOf(PropTypes.object),
  featureFlags: PropTypes.object,
}

const mapStateToProps = store => ({
  similarProperties: store.propertyAnalytics.similarProperties,
  featureFlags: store.featureFlags,
})

export default connect(mapStateToProps)(SimilarPropertiesCarousel)

// SectionPanel creates a div with an id of SIMILAR_PROPERTIES_ID
const SECTION_SELECTOR = `#${SIMILAR_PROPERTIES_ID}`
const LINK_ELM_ID = `${SIMILAR_PROPERTIES_ID}_link`

export const SimilarPropertiesButton = ({ similarProperties = [] }) =>
  hasSimilarProperties(similarProperties) ? (
    <ScrollSpy selector={SECTION_SELECTOR}>
      {({ _active, scrollTo }) => (
        <Button elmId={LINK_ELM_ID} isStretch role='primary' onClick={scrollTo}>
          {VIEW_SIMILAR_PROPERTIES}
        </Button>
      )}
    </ScrollSpy>
  ) : null

export const SimilarPropertiesLink = ({ similarProperties = [] }) =>
  hasSimilarProperties(similarProperties) ? (
    <ScrollSpy selector={SECTION_SELECTOR}>
      {({ _active, scrollTo }) => (
        <div
          className={cx('u-pointer-cursor')}
          data-elm-id={LINK_ELM_ID}
          onClick={scrollTo}
        >
          <Icon color='info' fitted name='eye' />
          <Text
            as='span'
            className={cx('u-ml-1')}
            color='info'
            content={VIEW_SIMILAR_PROPERTIES}
            size='tiny'
          />
        </div>
      )}
    </ScrollSpy>
  ) : null

SimilarPropertiesButton.propTypes = SimilarPropertiesLink.propTypes = {
  similarProperties: PropTypes.arrayOf(PropTypes.object),
}
